import { Button } from "@/components/ui/button";
import { AvisoNavy, Icon, StatusBadge, Tip } from "@/components/ds";
import { FuentesChips } from "@/components/capacitaciones-ia/FuentesChips";
import { cn } from "@/lib/utils";
import type { Programa, ProgramaFuente, ProgramaTematica } from "@/types/capacitaciones-ia";

/**
 * Interruptor único del versionado de Programas (el botón "Crear versión N+1").
 *
 * Está en `false` porque la función todavía no se va a usar. La feature está
 * completa y el backend no cambió: `POST /programas/{id}/versiones` sigue
 * respondiendo — se OCULTA, no se borra (mismo criterio que
 * `ENVIO_MANUAL_HABILITADO` y `PLAN_DE_ENVIOS_HABILITADO`).
 *
 * Gobierna el aviso "Congelado como evidencia" COMPLETO, no sólo su botón: ese
 * texto no describe un estado neutro, describe la regla que habilita crear la
 * versión siguiente ("el contenido no se edita: se crea una versión nueva").
 * Sin esa salida a la vista quedaba explicando un bloqueo del que no se puede
 * salir, así que se va entero.
 *
 * Lo que NO gobierna, porque es registro y no decisión: el número de versión de
 * cada fila (`v1`, `v2`) y el "Reemplazado por vN" de las archivadas. Son
 * historia de lo que ya pasó y se siguen mostrando.
 *
 * Tipado `: boolean` explícito por la misma razón que sus hermanos: con el
 * literal `false` TS estrecha el tipo y rompe el código que el archivo
 * conserva para cuando se vuelva a prender.
 */
export const VERSIONADO_HABILITADO: boolean = false;

/**
 * Recibe las DOS formas que manda el backend: `fecha_programada` como
 * "YYYY-MM-DD" y `enviado_at` como ISO8601 completo.
 *
 * El `T00:00:00` no es cosmético. Sin él, `new Date("2026-08-17")` se
 * interpreta como medianoche UTC y en Chile (UTC-4) cae el 16 a las 20:00:
 * la fila decía "Programada para el 16 ago" para una temática guardada el 17.
 * Mismo criterio que `PlanDeEnvios.parseFechaLocal`.
 */
function formatFechaCorta(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso.includes("T") ? iso : `${iso}T00:00:00`).toLocaleDateString("es-CL", {
    day: "2-digit",
    month: "short",
  });
}

/** Texto + ícono de la línea de plan bajo el objetivo — sólo aparece cuando
 * la temática ya tiene fecha (programada) o ya salió (enviada). Sin nombre de
 * destinatarios: resolverlos requiere `DestinatariosService`, fuera del
 * alcance de esta pantalla (esa resolución vive en el drawer "Programar
 * envíos", Ola 6). */
function lineaPlan(tema: ProgramaTematica): { texto: string; icono: string; tono: string } | null {
  if (tema.estado_negocio === "enviada" && tema.enviado_at) {
    return { texto: `Enviada el ${formatFechaCorta(tema.enviado_at)}`, icono: "check_circle", tono: "text-success" };
  }
  if (tema.estado_negocio === "programada" && tema.fecha_programada) {
    return { texto: `Programada para el ${formatFechaCorta(tema.fecha_programada)}`, icono: "event_upcoming", tono: "text-primary-hover" };
  }
  return null;
}

/**
 * Avance de respuestas de UNA temática ya enviada.
 *
 * Denominador congelado (design D10): `destinatarios_total` y `respondieron`
 * llegan resueltos del backend sobre los tokens realmente emitidos — no se
 * recalculan contra la dotación de hoy. Un trabajador desactivado después del
 * envío no le devuelve el 100% a la temática.
 *
 * Acá el umbral SÍ es un juicio legítimo, al revés que la barra de "Enviadas"
 * del listado: la capacitación ya salió a terreno, así que un 30% de respuesta
 * es una brecha de cumplimiento real y se pinta como tal. Los cortes son los
 * del prototipo (80 / 50).
 */
function BarraRespuestas({ tema }: { tema: ProgramaTematica }) {
  const total = tema.destinatarios_total;
  const pct = total > 0 ? Math.min(100, Math.round((tema.respondieron / total) * 100)) : 0;
  // Sin destinatarios no hay porcentaje que juzgar: un 0/0 en rojo acusaría de
  // incumplimiento a una temática que nunca tuvo a quién llegarle.
  const color =
    total === 0
      ? "var(--border-strong)"
      : pct >= 80
        ? "var(--primary)"
        : pct >= 50
          ? "var(--warning-bar)"
          : "var(--destructive)";

  return (
    <div className="flex w-[92px] shrink-0 flex-col gap-1">
      <span className="text-soft text-[10.5px] whitespace-nowrap">
        {total > 0 ? (
          <>
            <span className="fc-num">
              {tema.respondieron}/{total}
            </span>{" "}
            respondió
          </>
        ) : (
          "sin destinatarios"
        )}
      </span>
      <div className="bg-border-subtle h-[5px] overflow-hidden rounded-full">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

/**
 * Badge de estado de UNA temática.
 *
 * Sin punto y a 96px de ancho, las dos cosas por la misma razón: "Sin
 * programar" es la etiqueta más larga y mide 92.5px sin punto — 102.5px con
 * él — contra los 86px que tenía fijados. El texto desbordaba su propio fondo
 * y quedaba pegado a los bordes. El punto acá no aporta: no hay badges
 * vecinos de los que distinguirse, y el color de fondo ya dice el estado.
 *
 * El ancho sigue siendo fijo a propósito, para que los badges se alineen en
 * columna entre temáticas; sólo pasó a ser uno donde el texto entra.
 */
function badgeTematica(tema: ProgramaTematica) {
  const clase = "w-[96px] justify-center";
  if (tema.estado_negocio === "enviada") {
    return (
      <StatusBadge estado="subido" punto={false} className={clase}>
        Enviada
      </StatusBadge>
    );
  }
  if (tema.estado_negocio === "programada") {
    return (
      <StatusBadge estado="programada" punto={false} className={clase}>
        Programada
      </StatusBadge>
    );
  }
  return (
    <StatusBadge estado="pendiente" punto={false} className={clase}>
      Sin programar
    </StatusBadge>
  );
}

/**
 * `fuentes_enviadas`/`fuentes_consumidas` (D9, sdd/nuevo-programa-multifuente)
 * son crudos y no se muestran como números — sólo cuando dicen algo real: el
 * generador usó MENOS fuentes de las que se le mandaron. Con una sola fuente
 * enviada nunca hay nada que perder, y cuando ambos números coinciden es
 * ruido (confirmación de lo esperado, no una alerta). `fuentes_consumidas ===
 * null` es el caso "PFP legado" (D9) — igual de real: sólo se usó la
 * principal aunque se hayan mandado más.
 */
function avisoFuentesNoConsumidas(programa: Programa): string | null {
  const { fuentes_enviadas, fuentes_consumidas } = programa;
  if (fuentes_enviadas === null || fuentes_enviadas <= 1) return null;
  if (fuentes_consumidas === null) {
    return `Se enviaron ${fuentes_enviadas} fuentes, pero el generador sólo alcanzó a usar la principal.`;
  }
  if (fuentes_consumidas < fuentes_enviadas) {
    return `El generador usó ${fuentes_consumidas} de ${fuentes_enviadas} fuentes enviadas.`;
  }
  return null;
}

export interface ProgramaFilaExpandidaProps {
  programa: Programa;
  /** `activo`/`finalizado`/`archivado` — evidencia congelada a nivel Programa. */
  congelado: boolean;
  esArchivado: boolean;
  onCrearVersion: () => void;
  creandoVersion: boolean;
  /** Slot de acciones por temática. Hoy sólo "Enviar ahora": el contenido de
   * una temática no se edita desde acá. Las congeladas no lo reciben. */
  renderAccionesTematica?: (tema: ProgramaTematica) => React.ReactNode;
  /** Abre la ficha de una temática YA enviada (WU6a/WU6b —
   * sdd/capacitaciones-pantalla-unica): evidencia congelada, sólo lectura.
   * Una temática sin enviar no tiene ficha que mostrar todavía, así que la
   * fila sólo se vuelve clickeable cuando `estado_negocio === "enviada"`. */
  onAbrirFicha: (tema: ProgramaTematica) => void;
}

/**
 * Fila expandida del listado de Programas (D1). Muestra el aviso de
 * evidencia congelada (si aplica) y la lista de Temáticas con su estado.
 */
export function ProgramaFilaExpandida({
  programa,
  congelado,
  esArchivado,
  onCrearVersion,
  creandoVersion,
  renderAccionesTematica,
  onAbrirFicha,
}: ProgramaFilaExpandidaProps) {
  const enviadas = programa.temas.filter((t) => t.estado_negocio === "enviada").length;
  const avisoFuentes = avisoFuentesNoConsumidas(programa);

  // De dónde salió el temario. Va PRIMERO y separado del resto: es el origen
  // de todo lo que sigue — la IA leyó esas fuentes y de ahí escribió cada
  // temática. Sin esto, al abrir la fila las temáticas aparecen sin
  // procedencia y no hay forma de auditar contra qué se generaron.
  //
  // `fuentes` es el conjunto ORDENADO completo (documento_empresa + adhoc,
  // sdd/nuevo-programa-multifuente, design #1663) — reemplaza al `documento`
  // singular deprecado, que queda `null` en un Programa sólo-ad-hoc. Un
  // Programa siempre trae al menos una fuente (backfill de la migración), así
  // que `documento` sólo se usa como respaldo si por algún motivo `fuentes`
  // llegara vacío.
  const fuentes: ProgramaFuente[] =
    (programa.fuentes?.length ?? 0) > 0
      ? programa.fuentes
      : programa.documento
        ? [
            {
              id: -1,
              tipo: "documento_empresa" as const,
              etiqueta: programa.documento.label ?? programa.documento.filename_original ?? "Documento",
              orden: 0,
              empresa_documento_id: programa.documento.id,
              filename_original: programa.documento.filename_original,
              size_bytes: null,
              descarga_url: "",
            },
          ]
        : [];

  return (
    // `pl-8` sobre los 14px que ya pone el DataTable = 46px, la sangría del
    // prototipo. No es decoración: alinea el bloque desplegado con el nombre
    // del programa —que arranca después del chevron— en vez de pegarlo al
    // borde de la tarjeta. Sin ella, las temáticas se leen como hermanas de
    // la fila del programa y no como su contenido.
    <div className="flex flex-col gap-2.5 pl-8">
      {/* Sin tarjeta: es un encabezado del bloque expandido, no un objeto
          aparte. Encerrarlo en un panel blanco con borde lo ponía al mismo
          nivel visual que cada temática, que sí son objetos. Queda como en el
          prototipo — un divisor de 1px y aire, sobre el `bg-surface-muted` de
          la fila expandida (#fbfbfc, donde `border-subtle` sí se ve). */}
      {fuentes.length > 0 && (
        <div className="border-border-subtle flex flex-wrap items-start gap-2.5 border-b pb-2.5">
          <div className="flex shrink-0 flex-col gap-0.5 pr-1.5">
            <span className="text-soft text-[10.5px] font-semibold tracking-[0.04em] uppercase">
              {fuentes.length === 1 ? "Documento fuente" : "Documentos fuente"}
            </span>
            <span className="text-soft text-[10.5px]">De acá sale el temario</span>
          </div>
          <FuentesChips fuentes={fuentes} />
        </div>
      )}

      {avisoFuentes && (
        <div className="text-soft flex items-start gap-1.5 text-[11px] leading-[1.4]">
          <Icon name="info" size={14} className="text-icon-soft mt-[1px] shrink-0" />
          <span>{avisoFuentes}</span>
        </div>
      )}

      {/* El aviso se va ENTERO con el versionado: su texto no describe un
          estado neutro, describe la regla que habilita crear la versión
          siguiente. Sin esa salida, quedaba explicando un bloqueo del que no
          se puede salir. */}
      {VERSIONADO_HABILITADO && congelado && (
        <AvisoNavy
          icono="lock"
          titulo={esArchivado ? "Versión archivada" : "Congelado como evidencia"}
          detalle={
            <>
              {enviadas} de {programa.temas.length} temáticas ya salieron a terreno, con sus
              respuestas firmadas. El contenido no se edita: se crea una versión nueva y esta
              queda como respaldo.
            </>
          }
          // Una versión ya archivada no tiene salida: crear la siguiente se
          // hace desde la vigente, no desde el respaldo.
          accion={
            !esArchivado && (
              <Button
                variant="secondary"
                size="xs"
                onClick={onCrearVersion}
                disabled={creandoVersion}
                className="shrink-0"
              >
                {creandoVersion ? "Creando…" : `Crear versión ${programa.version + 1}`}
              </Button>
            )
          }
        />
      )}

      {programa.estado === "fallido" && (
        <div className="bg-card border-border-subtle flex items-start gap-3 rounded-lg border px-3 py-2.5">
          <Icon name="error" size={19} className="text-destructive shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-[12.5px] font-semibold">No pudimos generar las temáticas</div>
            <div className="text-soft text-[11.5px] leading-[1.45]">
              {programa.falla_motivo ?? "La generación no llegó a completarse."} El documento
              quedó libre: puedes volver a intentarlo desde «Nuevo programa».
            </div>
          </div>
        </div>
      )}

      {programa.temas.length === 0 ? (
        <p className="text-soft px-1 py-2 text-[12.5px]">
          {programa.estado === "generando"
            ? "La IA está generando las temáticas. Esto puede tardar unos minutos."
            : programa.estado === "fallido"
              ? "No alcanzó a generarse ninguna temática."
              : "Este programa todavía no tiene temáticas."}
        </p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {programa.temas.map((tema) => {
            const plan = lineaPlan(tema);
            const yaEnviada = tema.estado_negocio === "enviada";
            return (
              <div key={tema.id} className="bg-card border-border-subtle rounded-lg border">
                <div
                  onClick={yaEnviada ? () => onAbrirFicha(tema) : undefined}
                  className={cn(
                    // `flex-wrap`: con el avance de respuestas y "Ver
                    // capacitación" la fila de una temática enviada ya no entra
                    // en una línea en pantallas medianas. Envolver antes que
                    // comprimir, misma regla que la toolbar del DataTable.
                    "flex flex-wrap items-center gap-3 px-3 py-2.5",
                    // Sólo una temática ya enviada tiene ficha que mostrar —
                    // el resto de la fila queda igual, sin cursor ni hover.
                    yaEnviada && "hover:bg-primary-50 cursor-pointer transition-colors"
                  )}
                >
                  <span className="fc-num text-soft shrink-0 text-[11px]">
                    {String(tema.numero).padStart(2, "0")}
                  </span>
                  <div className="min-w-[200px] flex-1">
                    <div className="fc-truncate text-[12.5px] font-medium">{tema.titulo}</div>
                    {/* El objetivo se corta a una medida de lectura, no al
                        ancho disponible. `flex-1` le daba toda la sobra de la
                        fila —en un monitor ancho, más de 700px— así que una
                        sola línea gris cruzaba la pantalla entera y competía
                        con el título, que es lo que se lee primero. El texto
                        completo queda en el tooltip: es contexto, no un dato
                        que haga falta para decidir nada acá. */}
                    {tema.objetivo && (
                      <Tip texto={tema.objetivo}>
                        <div className="fc-truncate text-soft max-w-[460px] text-[11px]">
                          {tema.objetivo}
                        </div>
                      </Tip>
                    )}
                    {plan && (
                      <div className={`mt-0.5 flex items-center gap-1.5 ${plan.tono}`}>
                        <Icon name={plan.icono} size={13} />
                        <span className="fc-truncate text-[10.5px]">{plan.texto}</span>
                      </div>
                    )}
                  </div>
                  {/* Las preguntas por temática NO se muestran acá: es un
                      parámetro del programa completo, elegido una vez en
                      "Nuevo programa" y idéntico en todas sus temáticas.
                      Repetirlo fila por fila sugería que se decide de a una.
                      La duración sí se queda: varía entre temáticas.

                      Pero sin dato no se dibuja la pastilla. Un "—" sobre
                      fondo gris ocupa el lugar de una medida sin informar
                      ninguna: el hueco vacío se lee como un dato roto. */}
                  {tema.duracion_min !== null && (
                    <span className="fc-num bg-surface-muted text-muted-foreground shrink-0 rounded-md px-1.5 py-0.5 text-[10.5px]">
                      {tema.duracion_min} min
                    </span>
                  )}
                  {badgeTematica(tema)}
                  {/* Una temática enviada es la única que tiene resultados y
                      ficha. La fila entera ya navegaba a la ficha, pero eso no
                      se ve: sin un control visible el acceso al detalle
                      dependía de que el usuario adivinara que la fila era
                      clickeable. El botón lo hace explícito y deja la fila como
                      atajo, no como único camino. */}
                  {yaEnviada && (
                    <div className="flex shrink-0 items-center gap-2.5">
                      <BarraRespuestas tema={tema} />
                      <Button
                        variant="outline"
                        size="xs"
                        // La fila también navega: sin esto el click dispara los
                        // dos handlers y se navega dos veces al mismo destino.
                        onClick={(e) => {
                          e.stopPropagation();
                          onAbrirFicha(tema);
                        }}
                      >
                        <Icon name="fact_check" size={15} />
                        Ver capacitación
                      </Button>
                    </div>
                  )}
                  {/* `congelada` sigue mandando —una temática ya emitida no
                      admite acciones— pero deja de dibujar un candado. El
                      ícono ocupaba lugar para anunciar una restricción que el
                      usuario nunca intentó cruzar: al no haber nada que
                      editar, no había bloqueo que explicar. El estado ya lo
                      cuenta el badge ("Enviada") y la línea de plan. */}
                  {!tema.congelada && (
                    <div className="flex shrink-0 items-center justify-end gap-1">
                      {renderAccionesTematica?.(tema)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
